/**
 * AD-125: royalty tracking is read-only. Settlement is Phase 38.
 * No mutation endpoints exist; this page is intentionally display-only.
 */

import { Navigate } from 'react-router-dom';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch } from '@/api/http';
import { fmtCents, fmtBps, fmtTs } from './AlphaOmegaDashboardPage';

// ── Types ────────────────────────────────────────────────────────────────────

export interface RoyaltyEventResponse {
  event_id: string;
  company_id: string;
  period_start: number;
  period_end: number;
  royalty_bps: number;
  gross_revenue_cents: number;
  royalty_due_cents: number;
  status: string;
  settled_at: number | null;
  created_at: string;
}

interface RoyaltyTrackingResponse {
  events: RoyaltyEventResponse[];
  total: number;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

export function royaltyStatusColor(status: string): string {
  switch (status) {
    case 'pending':   return '#fbbf24';
    case 'settled':   return '#34d399';
    case 'overdue':   return '#f87171';
    case 'waived':    return '#94a3b8';
    default:          return 'var(--omega-ink)';
  }
}

export function fmtPeriod(start: number, end: number): string {
  const s = new Date(start * 1000).toLocaleDateString('he-IL', { month: 'short', year: 'numeric' });
  const e = new Date(end   * 1000).toLocaleDateString('he-IL', { month: 'short', year: 'numeric' });
  return `${s} – ${e}`;
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function AlphaOmegaRoyaltyTrackingPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const [companyFilter, setCompanyFilter] = useState('');
  const [periodStart,   setPeriodStart]   = useState('');

  const params = new URLSearchParams();
  if (companyFilter) params.set('company_id', companyFilter);
  if (periodStart)   params.set('period_start', periodStart);
  const qs = params.toString() ? `?${params.toString()}` : '';

  const { data, isPending, isError } = useQuery({
    queryKey: ['alpha-omega', 'royalty-tracking', companyFilter, periodStart],
    queryFn: () => apiFetch<RoyaltyTrackingResponse>(`/alpha-omega/royalty-tracking${qs}`),
    refetchInterval: 30_000,
  });

  const inputStyle: React.CSSProperties = {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid var(--omega-rim, rgba(255,255,255,0.12))',
    borderRadius: 4, color: 'var(--omega-ink)', fontFamily: 'var(--omega-mono)',
    fontSize: '8.5pt', padding: '4px 8px', outline: 'none',
  };

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-sans)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs crumbs={[
        { label: 'OMEGA',       to: '/omega' },
        { label: 'Alpha-Omega', to: '/omega/alpha-omega' },
        { label: 'Royalties' },
      ]} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, marginTop: 8, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0, fontSize: '12pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Royalty Tracking
        </h1>
        {/* AD-125 read-only badge */}
        <span style={{
          fontSize: '7.5pt', padding: '2px 8px',
          border: '1px solid rgba(148,163,184,0.4)',
          color: '#94a3b8', letterSpacing: '0.06em', fontWeight: 700, borderRadius: 3,
        }}>
          AD-125 · READ-ONLY · SETTLEMENT PHASE 38
        </span>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <label style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', fontFamily: 'var(--omega-mono)' }}>Company:</label>
          <input value={companyFilter} onChange={e => setCompanyFilter(e.target.value)} placeholder="company_id" style={{ ...inputStyle, width: 140 }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <label style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', fontFamily: 'var(--omega-mono)' }}>From date (unix):</label>
          <input value={periodStart} onChange={e => setPeriodStart(e.target.value)} placeholder="unix ts" style={{ ...inputStyle, width: 100 }} />
        </div>
      </div>

      {isPending && <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading…</div>}
      {isError   && <div style={{ color: '#f87171', fontSize: '9pt' }}>Error loading royalties.</div>}

      {data && (
        <>
          <div style={{ fontSize: '8pt', color: 'var(--omega-ink-subtle)', fontFamily: 'var(--omega-mono)', marginBottom: 12 }}>
            {data.total} Events
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 860 }}>
            {data.events.map(ev => (
              <div
                key={ev.event_id}
                style={{
                  display: 'grid', gridTemplateColumns: '1fr 100px 100px 100px 80px 80px',
                  gap: 10, padding: '10px 14px',
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid var(--omega-rim, rgba(255,255,255,0.08))',
                  borderRadius: 4,
                }}
              >
                <span style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', fontFamily: 'var(--omega-mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {ev.company_id.slice(0, 18)}
                </span>
                <span style={{ fontSize: '8.5pt', fontFamily: 'var(--omega-mono)', color: '#94a3b8' }}>{fmtBps(ev.royalty_bps)}</span>
                <span style={{ fontSize: '8.5pt', fontFamily: 'var(--omega-mono)', color: '#34d399' }}>{fmtCents(ev.gross_revenue_cents)}</span>
                <span style={{ fontSize: '8.5pt', fontFamily: 'var(--omega-mono)', color: '#a78bfa' }}>{fmtCents(ev.royalty_due_cents)}</span>
                <span style={{ fontSize: '8pt', fontFamily: 'var(--omega-mono)', color: royaltyStatusColor(ev.status), textTransform: 'uppercase' }}>{ev.status}</span>
                <span style={{ fontSize: '7.5pt', color: 'var(--omega-ink-subtle)', fontFamily: 'var(--omega-mono)' }}>{fmtTs(ev.period_start)}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
